import { TFunction } from 'i18next'
import { ListOrderedIcon, RulerIcon } from 'lucide-react'
import { FC, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PkgReqTypeEnum } from '@/api/package-req/types/PkgReq'
import { AppDialog } from '@/components/AppDialog'
import { Divider } from '@/components/Divider'
import PhotoIcon from '@/components/icons/pkg-photo.svg?react'
import ShrinkIcon from '@/components/icons/pkg-shrink.svg?react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

const getTypeToData = (t: TFunction) => ({
  [PkgReqTypeEnum.ShrinkRequest]: {
    title: t('pkgReqs.confirmDialog.shrinkTitle'),
    icon: ShrinkIcon,
    description: t('pkgReqs.confirmDialog.shrinkDescription'),
    buttonText: t('pkgReqs.confirmDialog.shrinkButton'),
    canAddComment: true,
  },
  [PkgReqTypeEnum.PhotoRequest]: {
    title: t('pkgReqs.confirmDialog.photoTitle'),
    icon: PhotoIcon,
    description: t('pkgReqs.confirmDialog.photoDescription'),
    buttonText: t('pkgReqs.confirmDialog.photoButton'),
    canAddComment: true,
  },
  [PkgReqTypeEnum.CountRequest]: {
    title: t('pkgReqs.confirmDialog.countTitle'),
    icon: ListOrderedIcon,
    description: t('pkgReqs.confirmDialog.countDescription'),
    buttonText: t('pkgReqs.confirmDialog.countButton'),
    canAddComment: true,
  },
  [PkgReqTypeEnum.MeasureRequest]: {
    title: t('pkgReqs.confirmDialog.measureTitle'),
    icon: RulerIcon,
    description: t('pkgReqs.confirmDialog.measureDescription'),
    buttonText: t('pkgReqs.confirmDialog.measureButton'),
    canAddComment: true,
  },
})
interface PkgRequestConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (comment: string) => void
  isLoading?: boolean
  requestType: PkgReqTypeEnum
  showFeeNote?: boolean
}
export const PkgRequestConfirmDialog: FC<PkgRequestConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  requestType,
  showFeeNote = true,
}) => {
  const { t } = useTranslation()
  const [comment, setComment] = useState('')
  const [showInvalidCharTooltip, setShowInvalidCharTooltip] = useState(false)
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const typeToData = getTypeToData(t)
  // Check if character is valid (English letters, numbers, and common symbols)
  const isValidChar = (char: string): boolean => {
    // Allow ASCII characters from 32 (space) to 126 (~)
    // This includes: letters (A-Z, a-z), numbers (0-9), and common symbols
    const charCode = char.charCodeAt(0)
    return charCode >= 32 && charCode <= 126
  }
  const data = typeToData[requestType as keyof ReturnType<typeof getTypeToData>]!
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value

    // Filter out invalid characters
    const filteredValue = inputValue
      .split('')
      .filter(char => isValidChar(char))
      .join('')

    // Check if any characters were filtered out (invalid characters detected)
    if (filteredValue.length < inputValue.length) {
      // Show tooltip
      setShowInvalidCharTooltip(true)

      // Clear previous timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }

      // Hide tooltip after 3 seconds
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowInvalidCharTooltip(false)
      }, 3000)
    } else {
      // Hide tooltip if no invalid characters
      setShowInvalidCharTooltip(false)
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }

    setComment(filteredValue)
  }

  const handleSubmit = async () => {
    onConfirm(comment)
  }
  const Icon = data.icon
  return (
    <AppDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      title={
        <div className='space-y-4 text-center'>
          <div className='mx-auto h-6 w-6'>
            <Icon className='text-icon-primary h-full w-full' />
          </div>
          <div className='text-lg font-medium'>{data.title}</div>
        </div>
      }
      className='!max-w-lg'
      dialogContentClassName='p-0'
    >
      <div className='flex flex-col items-center gap-6 px-6 pt-2 text-center'>
        <p className='text-text-secondary mx-auto max-w-[250px] text-sm leading-relaxed'>{data.description}</p>
        {data.canAddComment && (
          <TooltipProvider>
            <Tooltip open={showInvalidCharTooltip}>
              <TooltipTrigger asChild>
                <div className='w-full'>
                  <Textarea
                    id='warehouse-notes'
                    label={t('pkgReqs.confirmDialog.warehouseNotesLabel')}
                    placeholder={t('pkgReqs.confirmDialog.warehouseNotesPlaceholder')}
                    value={comment}
                    onChange={handleInputChange}
                    maxLength={500}
                    className='w-full'
                    supportingText={t('pkgReqs.confirmDialog.warehouseNotesSupportingText')}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side='top' dir='rtl'>
                {t('pkgReqs.confirmDialog.invalidCharsTooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {showFeeNote && (
          <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-3 py-2'>
            <p className='text-text-secondary text-xs'>{t('pkgReqs.confirmDialog.feeNote')}</p>
          </div>
        )}
      </div>
      <Divider className='my-6' />
      <div className='flex justify-center px-6 pb-6'>
        <Button loading={isLoading} onClick={handleSubmit} disabled={isLoading} className='w-full max-w-[335px]' size='lg'>
          {data.buttonText}
        </Button>
      </div>
    </AppDialog>
  )
}
